/**
 * Product Price Component
 * Matches Shapermint.com pricing layout
 */

'use client';

import { useState } from 'react';
import styled from '@emotion/styled';

import { useTranslation } from '@/lib/i18n/client';
import { useIntl } from '@/lib/intl';
import { CurrencyAmount } from '@/domain/value-objects/currency-amount.vo';
import type { Discount } from '@template-project/storefront-bff-client';
import { formatMoney } from '@/lib/utils/format-money';

const PriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.space.sm};
`;

const PriceRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
  flex-wrap: wrap;
`;

const StrikethroughPrice = styled.span`
  font-size: 18px;
  text-decoration: line-through;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: ${({ theme }) => theme.typography.config.weight.regular};
`;

const TooltipWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const InfoIcon = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.text.secondary};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 10px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  cursor: pointer;
  padding: 0;
  
  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
  }
`;

const Tooltip = styled.div<{ visible: boolean }>`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: ${({ theme }) => theme.colors.text.primary};
  color: ${({ theme }) => theme.colors.background.primary};
  padding: ${({ theme }) => `${theme.space.xs} ${theme.space.sm}`};
  border-radius: ${({ theme }) => theme.radius.small};
  font-size: 11px;
  white-space: nowrap;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
  transition: opacity 0.2s, visibility 0.2s;
  z-index: 10;
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const CurrentPrice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1px;
`;

const CurrentPriceSymbol = styled.span`
  font-size: 14px;
  font-weight: ${({ theme }) => theme.typography.config.weight.regular};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1;
  margin-top: 2px;
`;

const CurrentPriceAmount = styled.span`
  font-size: 36px;
  font-weight: ${({ theme }) => theme.typography.config.weight.heavy};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1;
  
  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    font-size: 32px;
  }
`;

const CurrentPriceCents = styled.span`
  font-size: 14px;
  font-weight: ${({ theme }) => theme.typography.config.weight.heavy};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1;
  margin-top: 2px;
`;

const DiscountBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  background-color: rgba(239, 214, 210, 0.5);
  color: ${({ theme }) => theme.colors.semantic.urgent.color};
  border-radius: 20px;
  font-size: 12px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  letter-spacing: 0.5px;
  line-height: 16px;
  text-transform: uppercase;
  width: fit-content;
`;

const InstallmentHint = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    font-size: 12px;
  }
`;

const AfterpayLogo = styled.img`
  height: 18px;
  width: auto;
  margin-left: 4px;
  vertical-align: middle;
`;

const AFTERPAY_LOGO_URL =
  'https://cdn.shopify.com/s/files/1/0021/4889/2732/files/afterpay-logo.svg?v=1728043576';
const INSTALLMENT_COUNT = 4;

export interface ProductPriceProps {
  price: CurrencyAmount;
  /**
   * Compare-at price (MSRP). Accepts `null` so callers can pass through API
   * payloads that explicitly send `null` for "no compare-at" without having
   * to coerce to undefined.
   */
  compareAtPrice?: CurrencyAmount | null;
  /**
   * Pricing-go unified discounts envelope. When provided, the percentage and
   * savings shown in the badge are read from this array instead of being
   * recomputed locally from `compareAtPrice`. Falls back to local math when
   * absent (e.g. cart-side rendering with only a compare-at value).
   */
  discounts?: Discount[];
  /** @deprecated Use useIntl() context instead. Only used as override for testing. */
  currency?: string;
}

/**
 * Formats price into parts for granular styling.
 * Returns { symbol, dollars, cents } to allow separate styling of each part.
 */
function formatPriceParts(cents: number, currency: string, locale: string) {
  const amount = cents / 100;
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const parts = formatter.formatToParts(amount);
  const symbol = parts.find((p) => p.type === 'currency')?.value || '$';
  const integerPart = parts.find((p) => p.type === 'integer')?.value || '0';
  const fractionPart = parts.find((p) => p.type === 'fraction')?.value || '00';

  return { symbol, dollars: integerPart, cents: fractionPart };
}

export function ProductPrice({ price, compareAtPrice, discounts, currency: currencyOverride }: ProductPriceProps) {
  const { t } = useTranslation('pdp');
  const { countryConfig } = useIntl();
  const [showTooltip, setShowTooltip] = useState(false);

  const currency = currencyOverride || countryConfig.currency.code;
  const locale = countryConfig.locale;

  const displayPriceCents = price.display;
  const displayComparePriceCents = compareAtPrice?.display;
  const hasDiscount = !!compareAtPrice && (displayComparePriceCents ?? 0) > displayPriceCents;

  const unifiedSavings = computeSavingsFromDiscounts(discounts, displayPriceCents);
  const discountPercent = unifiedSavings?.percent
    ?? (hasDiscount
      ? Math.round(((displayComparePriceCents! - displayPriceCents) / displayComparePriceCents!) * 100)
      : 0);
  const savingsAmount = unifiedSavings?.amount
    ?? (hasDiscount ? displayComparePriceCents! - displayPriceCents : 0);

  const priceParts = formatPriceParts(displayPriceCents, currency, locale);
  const currencyFormat = countryConfig.currency.format;

  const formatWithConfigSymbol = (cents: number) => {
    const parts = formatPriceParts(cents, currency, locale);
    return `${currencyFormat}${parts.dollars}.${parts.cents}`;
  };

  return (
    <PriceContainer>
      {/* Price row: Strikethrough + tooltip + Current price */}
      <PriceRow>
        {hasDiscount && compareAtPrice && (
          <>
            <StrikethroughPrice>
              {formatWithConfigSymbol(displayComparePriceCents!)}
            </StrikethroughPrice>
            <TooltipWrapper>
              <InfoIcon
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
                onClick={() => setShowTooltip(!showTooltip)}
                aria-label="Price information"
              >
                i
              </InfoIcon>
              <Tooltip visible={showTooltip}>
                The strikethrough price is the Manufacturer&apos;s Suggested Retail Price.
              </Tooltip>
            </TooltipWrapper>
          </>
        )}

        {/* Current price */}
        <CurrentPrice>
          <CurrentPriceSymbol>{countryConfig.currency.format}</CurrentPriceSymbol>
          <CurrentPriceAmount>{priceParts.dollars}</CurrentPriceAmount>
          <CurrentPriceCents>{priceParts.cents}</CurrentPriceCents>
        </CurrentPrice>
      </PriceRow>

      {/* Discount badge - on its own row, fitting text */}
      {hasDiscount && (
        <DiscountBadge>
          {discountPercent}% OFF - SAVE {formatWithConfigSymbol(savingsAmount)}
        </DiscountBadge>
      )}

      {/* BNPL installment hint */}
      {displayPriceCents > 0 && (
        <InstallmentHint>
          <span>
            or {INSTALLMENT_COUNT} interest-free payments of{' '}
            {formatWithConfigSymbol(Math.round(displayPriceCents / INSTALLMENT_COUNT))}{' '}
            with
          </span>
          <AfterpayLogo src={AFTERPAY_LOGO_URL} alt="Afterpay" loading="lazy" />
        </InstallmentHint>
      )}
    </PriceContainer>
  );
}

/**
 * Pull the unit-applicable savings (amount in display cents and percent) from
 * the unified discounts envelope. Prefers explicit `amount`/`percent` fields
 * on the discount; falls back to a percent_off discount when only a percent
 * is provided.
 */
function computeSavingsFromDiscounts(
  discounts: Discount[] | undefined,
  displayPriceCents: number,
): { amount?: number; percent?: number } | null {
  if (!discounts || discounts.length === 0) return null;
  const unitDiscounts = discounts.filter((d) => d.appliesTo === 'unit');
  if (unitDiscounts.length === 0) return null;

  const amountOff = unitDiscounts.find((d) => d.type === 'amount_off' && d.amount);
  if (amountOff?.amount) {
    const percent = amountOff.percent
      ?? Math.round((amountOff.amount.display / (amountOff.amount.display + displayPriceCents)) * 100);
    return { amount: amountOff.amount.display, percent };
  }

  const percentOff = unitDiscounts.find((d) => d.type === 'percent_off' && typeof d.percent === 'number');
  if (percentOff && typeof percentOff.percent === 'number') {
    const amount = Math.round((displayPriceCents * percentOff.percent) / (100 - percentOff.percent));
    return { amount, percent: percentOff.percent };
  }
  return null;
}

// formatMoney is imported above; the helper centralises Intl.NumberFormat
// behaviour so amounts > $1,000 keep cents (resolves PR #213 #3111297516).
void formatMoney;

